import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiService, UserResponse, DevicesResponse } from '../services/api_url';

// Версия стора
const STORE_VERSION = 10;

interface AppState {
    // Версия стора для миграций
    _storeVersion?: number;

    // User data
    userData: UserResponse | null;
    telegramId: string | null;
    isAuthenticated: boolean;
    authChecked: boolean;

    // Devices data
    devices: DevicesResponse | null;

    // UI state
    isLoading: boolean;
    isUserLoading: boolean;
    isDevicesLoading: boolean;
    error: string | null;
    userError: string | null;
    devicesError: string | null;

    // Actions
    setUserData: (data: UserResponse | null) => void;
    setTelegramId: (id: string | null) => void;
    setIsAuthenticated: (auth: boolean) => void;
    setAuthChecked: (checked: boolean) => void;

    loadUserData: (telegramId: string) => Promise<UserResponse | null>;
    loadDevices: (telegramId: string) => Promise<DevicesResponse | null>;
    updateDeviceName: (deviceId: number, newName: string) => void;
    updateUserEmail: (email: string) => void;
    refreshAllData: (telegramId: string) => Promise<void>;

    // Управление устройствами
    addDevice: (device: any) => void;
    removeDevice: (deviceId: number) => void;

    reset: () => void;
}

const combineError = (userError: string | null, devicesError: string | null): string | null => {
    return devicesError || userError || null;
};

const setLoadingState = (set: any, partial: Partial<Pick<AppState, 'isUserLoading' | 'isDevicesLoading' | 'userError' | 'devicesError'>>) => {
    set((state: AppState) => {
        const nextUserError = partial.userError !== undefined ? partial.userError : state.userError;
        const nextDevicesError = partial.devicesError !== undefined ? partial.devicesError : state.devicesError;
        const nextUserLoading = partial.isUserLoading !== undefined ? partial.isUserLoading : state.isUserLoading;
        const nextDevicesLoading = partial.isDevicesLoading !== undefined ? partial.isDevicesLoading : state.isDevicesLoading;

        return {
            ...partial,
            isUserLoading: nextUserLoading,
            isDevicesLoading: nextDevicesLoading,
            isLoading: nextUserLoading || nextDevicesLoading,
            error: combineError(nextUserError, nextDevicesError),
        };
    });
};

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            _storeVersion: STORE_VERSION,

            userData: null,
            telegramId: null,
            isAuthenticated: false,
            authChecked: false,
            devices: null,
            isLoading: false,
            isUserLoading: false,
            isDevicesLoading: false,
            error: null,
            userError: null,
            devicesError: null,

            setUserData: (data) => set({ userData: data }),
            setTelegramId: (id) => set({ telegramId: id }),
            setIsAuthenticated: (auth) => set({ isAuthenticated: auth }),
            setAuthChecked: (checked) => set({ authChecked: checked }),

            loadUserData: async (telegramId: string) => {
                setLoadingState(set, { isUserLoading: true, userError: null });

                try {
                    const data = await apiService.getUserData(telegramId);

                    if (data) {
                        setLoadingState(set, { isUserLoading: false, userError: null });
                        set({ userData: data, telegramId });
                        return data;
                    }

                    setLoadingState(set, { isUserLoading: false, userError: 'Пользователь не найден' });
                    return null;
                } catch (error) {
                    setLoadingState(set, { isUserLoading: false, userError: 'Не удалось загрузить данные' });
                    throw error;
                }
            },

            loadDevices: async (telegramId: string) => {
                setLoadingState(set, { isDevicesLoading: true, devicesError: null });

                try {
                    const response = await apiService.getUserDevices(telegramId);
                    set({ devices: response });
                    setLoadingState(set, { isDevicesLoading: false, devicesError: null });
                    return response;
                } catch (error) {
                    setLoadingState(set, { isDevicesLoading: false, devicesError: 'Не удалось загрузить устройства' });
                    return null;
                }
            },

            updateDeviceName: (deviceId: number, newName: string) => {
                set((state) => {
                    if (!state.devices?.devices) return state;

                    const updatedDevices = state.devices.devices.map(device =>
                        device.id === deviceId ? { ...device, name: newName } : device
                    );

                    return {
                        ...state,
                        devices: {
                            ...state.devices,
                            devices: updatedDevices
                        }
                    };
                });
            },

            updateUserEmail: (email: string) => {
                set((state) => ({
                    ...state,
                    userData: state.userData ? { ...state.userData, email } : null
                }));
            },

            refreshAllData: async (telegramId: string) => {
                setLoadingState(set, {
                    isUserLoading: true,
                    isDevicesLoading: true,
                    userError: null,
                    devicesError: null,
                });

                try {
                    const [userData, devices] = await Promise.all([
                        apiService.getUserData(telegramId),
                        apiService.getUserDevices(telegramId)
                    ]);

                    set({
                        userData,
                        devices,
                    });

                    setLoadingState(set, {
                        isUserLoading: false,
                        isDevicesLoading: false,
                        userError: userData ? null : 'Пользователь не найден',
                        devicesError: null,
                    });
                } catch (error) {
                    setLoadingState(set, {
                        isUserLoading: false,
                        isDevicesLoading: false,
                        userError: 'Не удалось обновить данные пользователя',
                        devicesError: 'Не удалось обновить устройства',
                    });
                }
            },

            addDevice: (device: any) => {
                const { devices } = get();
                if (!devices) return;

                const updatedDevices = {
                    ...devices,
                    devices: [...devices.devices, device],
                    total: devices.total + 1,
                    configured_count: device.is_configured
                        ? devices.configured_count + 1
                        : devices.configured_count,
                    pending_count: !device.is_configured
                        ? devices.pending_count + 1
                        : devices.pending_count
                };

                set({ devices: updatedDevices });
            },

            removeDevice: (deviceId: number) => {
                const { devices } = get();
                if (!devices) return;

                const deviceToRemove = devices.devices.find((d: any) => d.id === deviceId);
                if (!deviceToRemove) return;

                const updatedDevices = {
                    ...devices,
                    devices: devices.devices.filter((d: any) => d.id !== deviceId),
                    total: devices.total - 1,
                    configured_count: deviceToRemove.is_configured
                        ? devices.configured_count - 1
                        : devices.configured_count,
                    pending_count: !deviceToRemove.is_configured
                        ? devices.pending_count - 1
                        : devices.pending_count
                };

                set({ devices: updatedDevices });
            },

            reset: () => {
                set({
                    userData: null,
                    telegramId: null,
                    isAuthenticated: false,
                    authChecked: false,
                    devices: null,
                    isLoading: false,
                    isUserLoading: false,
                    isDevicesLoading: false,
                    error: null,
                    userError: null,
                    devicesError: null,
                });
            }
        }),
        {
            name: 'app-storage',
            storage: createJSONStorage(() => sessionStorage),

            migrate: (persistedState: any, version: number) => {
                const state = persistedState as AppState;

                if (state._storeVersion !== STORE_VERSION) {
                    console.log(`🔄 Миграция кэша с версии ${state._storeVersion} на ${STORE_VERSION}`);

                    return {
                        _storeVersion: STORE_VERSION,
                        telegramId: state.telegramId,
                        isAuthenticated: state.isAuthenticated,
                        authChecked: false,
                        userData: null,
                        devices: null,
                        isLoading: false,
                        isUserLoading: false,
                        isDevicesLoading: false,
                        error: null,
                        userError: null,
                        devicesError: null,
                    };
                }

                return state;
            },

            version: STORE_VERSION,

            partialize: (state) => ({
                _storeVersion: state._storeVersion,
                userData: state.userData,
                telegramId: state.telegramId,
                isAuthenticated: state.isAuthenticated,
                devices: state.devices,
            })
        }
    )
);